import { createReducer, RequestStatus } from '@lib/plugin-redux-core';
import {
  CREATE_FOLDER_USE_CASE,
  CREATE_FORM_USE_CASE,
  DELETE_FOLDER_USE_CASE,
  DELETE_FORM_USE_CASE,
  SpaceObjects,
  UpdateFolderUseCaseInput,
  UpdateFormUseCaseInput,
  UPDATE_FOLDER_USE_CASE,
  FolderSummary,
  FormSummary,
  UPDATE_FORM_TITLE_USE_CASE,
  SEARCH_SPACE_OBJECTS_USE_CASE,
  MOVE_FOLDER_PICKER_USE_CASE,
  GET_FOLDER_BY_ID_USE_CASE,
  FIND_GLOBAL_FORMS_USE_CASE,
  GlobalForm,
  GET_TAGS_USE_CASE,
  Tag,
  FormCategory,
  GET_FORM_CATEGORIES_USE_CASE,
  Form,
} from '@module/form';
import { UseCaseError } from '@digibricks/domains';
import {
  handleCloneFormUseCase,
  handleCreateFolderUseCase,
  handleCreateFormUseCase,
  handleDeleteFolderUseCase,
  handleDeleteFormUseCase,
  handleFindFormByIdUseCase,
  handleGetFolderByIdUseCase,
  handleGetFormCategoriesUseCase,
  handleGetGlobalFormsUseCase,
  handleGetTagUseCase,
  handleLoadMoreSpaceObjectsUseCase,
  handleMoveFolderPickerUseCase,
  handleSearchSpaceObjectsUseCase,
  handleUpdateFolderUseCase,
  handleUpdateFormTitleUseCase,
} from './space.handler';

export type SpacePageInitialState = {
  error?: UseCaseError<any>;
};

//UseCases State ---------------
export type SearchSpaceObjectsUseCaseStates = {
  formsAndFoldersSearchOptions: SpaceObjects;
  searchFormsAndFoldersStatus: RequestStatus;
  moveFolderPickerSpaceObjects?: SpaceObjects;
  moveFolderPickerStatus: RequestStatus;
};
export type GetTagsUseCaseState = {
  tags: Tag[];
  getTagsStatus: RequestStatus;
  isCreateFormButtonLoading: boolean;
};

export type GetFormCategoriesUseCaseState = {
  formCategories: FormCategory[];
  getFormCategoriesStatus: RequestStatus;
  isCreateFormButtonLoading: boolean;
};

export type CreateFolderUseCaseState = {
  createdFolderSummary: FolderSummary;
  createFolderStatus: RequestStatus;
};

export type UpdateFolderUseCaseState = {
  updatedFolderSummary: UpdateFolderUseCaseInput;
  updateFolderSummaryStatus: RequestStatus;
};

export type GetFolderByIdUseCase = {
  spaceObjects: SpaceObjects;
  getFolderByIdStatus: RequestStatus;
};

export type FindFormByIdsUseCase = {
  importingFormIds: string[];
  importingFormId: string;
  findFormByIdsStatus: RequestStatus;
  hasErrorForm: boolean;
};

export type DeleteFolderUseCaseState = {
  deletedFolderId: string;
  deleteFolderStatus: RequestStatus;
};

export type CreateFormUseCaseState = {
  createdFormSummary: FormSummary;
  createFormStatus: RequestStatus;
  newFormId: string;
};

export type UpdateFormUseCaseState = {
  updatedFormSummary: UpdateFormUseCaseInput;
  updateFormSummaryStatus: RequestStatus;
};

export type DeleteFormUseCaseState = {
  deletedFormId: string;
  deleteFormStatus: RequestStatus;
};

export type CreateFolderModalState = {
  createFolderModalStatus: boolean;
};

export type GetGlobalFormState = {
  globalForms: GlobalForm[];
  getGlobalFormsStatus: RequestStatus;
};

export type CloneFormState = {
  clonedFormStatus: RequestStatus;
};

export type FindFormByIdUseCaseState = {
  formSelected: Form;
  hasEssayOrFillInBlankQuestion: boolean;
  findFormByIdStatus: RequestStatus;
};

export type LoadMoreSpaceObjectsState = {
  folderNextToken: string | null;
  formNextToken: string | null;
  loadingMoreFoldersStatus: RequestStatus;
  loadingMoreFormsStatus: RequestStatus;
};

type FolderUseCasesStates = CreateFolderUseCaseState &
  UpdateFolderUseCaseState &
  DeleteFolderUseCaseState &
  GetFolderByIdUseCase &
  FindFormByIdsUseCase;

type FormUseCasesStates = CreateFormUseCaseState &
  UpdateFormUseCaseState &
  DeleteFormUseCaseState &
  FindFormByIdUseCaseState;

export type SpacePageState = SpacePageInitialState &
  SearchSpaceObjectsUseCaseStates &
  FolderUseCasesStates &
  FormUseCasesStates &
  CreateFolderModalState &
  GetGlobalFormState &
  GetTagsUseCaseState &
  GetFormCategoriesUseCaseState &
  CloneFormState &
  LoadMoreSpaceObjectsState;

export const initialState: Partial<SpacePageState> = {
  error: null,
  folderNextToken: null,
  formNextToken: null,
  loadingMoreFoldersStatus: RequestStatus.RESET,
  loadingMoreFormsStatus: RequestStatus.RESET,
  moveFolderPickerStatus: RequestStatus.RESET,
};

export const LOADING_SPACE = 'LOADING_SPACE';
export const LOAD_MORE_SPACE_OBJECTS_USE_CASE = 'LOAD_MORE_SPACE_OBJECTS_USE_CASE';
export const spaceObjectsReducer = createReducer(initialState, [
  {
    name: SEARCH_SPACE_OBJECTS_USE_CASE,
    ...handleSearchSpaceObjectsUseCase,
  },
  {
    name: MOVE_FOLDER_PICKER_USE_CASE,
    ...handleMoveFolderPickerUseCase,
  },
  {
    name: GET_FOLDER_BY_ID_USE_CASE,
    ...handleGetFolderByIdUseCase,
  },
  {
    name: FIND_GLOBAL_FORMS_USE_CASE,
    ...handleGetGlobalFormsUseCase,
  },
  {
    name: CREATE_FOLDER_USE_CASE,
    ...handleCreateFolderUseCase,
  },
  {
    name: UPDATE_FOLDER_USE_CASE,
    ...handleUpdateFolderUseCase,
  },
  {
    name: DELETE_FOLDER_USE_CASE,
    ...handleDeleteFolderUseCase,
  },
  {
    name: CREATE_FORM_USE_CASE,
    ...handleCreateFormUseCase,
  },
  {
    name: UPDATE_FORM_TITLE_USE_CASE,
    ...handleUpdateFormTitleUseCase,
  },
  {
    name: DELETE_FORM_USE_CASE,
    ...handleDeleteFormUseCase,
  },
  {
    name: GET_TAGS_USE_CASE,
    ...handleGetTagUseCase,
  },
  {
    name: GET_FORM_CATEGORIES_USE_CASE,
    ...handleGetFormCategoriesUseCase,
  },
  {
    ...handleCloneFormUseCase,
  },
  {
    ...handleFindFormByIdUseCase,
  },
  {
    name: LOAD_MORE_SPACE_OBJECTS_USE_CASE,
    ...handleLoadMoreSpaceObjectsUseCase,
  },
]);
